import React from "react";
import { get } from "lodash";
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import ImageListItemBar from "@mui/material/ImageListItemBar";
import Link from "@mui/material/Link";
import Paper from '@mui/material/Paper';
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";

const MidjourneyView = (props: {
  result: any;
  loading: boolean;
  error: string;
  status: string;
  history: any[];
  onAction: (messageId: string, customId: string) => void;
}) => {
  const { result, loading, error, status, history, onAction } = props;

  const getImages = () => {
    if (!result) return [];
    const attachments = get(result, "attachments") || [];
    return attachments.filter((a: any) => {
      const contentType = get(a, "content_type", "");
      return contentType.startsWith("image/");
    });
  };

  const getJobUrl = () => {
    const components = get(result, "components") || [];
    for (const row of components) {
      const buttons = get(row, "components") || [];
      for (const btn of buttons) {
        if (get(btn, "style") === 5 && get(btn, "url", "").includes("midjourney.com")) {
          return get(btn, "url");
        }
      }
    }
    return null;
  };

  const getActionButtons = () => {
    if (!result) return [];
    const components = get(result, "components") || [];
    const rows: any[][] = [];
    for (const row of components) {
      const buttons = get(row, "components") || [];
      const actionBtns = buttons.filter((btn: any) => {
        const style = get(btn, "style");
        // style 5 = link buttons (Web), skip those
        // style 2 = default action buttons (U1-U4, V1-V4, etc.)
        return style !== 5 && get(btn, "custom_id");
      });
      if (actionBtns.length > 0) {
        rows.push(actionBtns);
      }
    }
    return rows;
  };

  const images = getImages();
  const content = get(result, "content", "");
  const jobUrl = result ? getJobUrl() : null;
  const actionRows = getActionButtons();
  const messageId = get(result, "id");

  return (
    <Grid item xs={12}>
      <Container maxWidth={false} sx={{ background: "#F6F6F6", marginTop: "10px", padding: "10px" }}>
        <Box sx={{ width: "100%" }}>
          <Paper sx={{ padding: "10px" }}>
            {status && (
              <Typography variant="body2" color="text.secondary" sx={{ marginBottom: "10px" }}>
                Status: {status}
              </Typography>
            )}

            {error && (
              <Alert severity="error" sx={{ marginBottom: "10px" }}>
                {error}
              </Alert>
            )}

            {content && (
              <Typography variant="body2" sx={{ marginBottom: "10px", wordBreak: "break-word" }}>
                {content}
              </Typography>
            )}

            {jobUrl && (
              <Typography variant="body2" sx={{ marginBottom: "10px" }}>
                <Link href={jobUrl} target="_blank" rel="noopener noreferrer">
                  View on Midjourney
                </Link>
              </Typography>
            )}

            {images.length > 0 && (
              <ImageList sx={{ width: "100%" }} cols={1}>
                {images.map((img: any) => (
                  <ImageListItem key={get(img, "id")}>
                    <a href={get(img, "url")} target="_blank" rel="noopener noreferrer">
                      <img
                        src={get(img, "url")}
                        alt={get(img, "filename", "midjourney image")}
                        loading="lazy"
                        style={{ maxWidth: "100%", cursor: "pointer" }}
                      />
                    </a>
                    <ImageListItemBar
                      title={get(img, "filename")}
                      subtitle={`${get(img, "width", "")}x${get(img, "height", "")} - ${(get(img, "size", 0) / 1024 / 1024).toFixed(1)}MB`}
                      position="bottom"
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            )}

            {actionRows.length > 0 && (
              <Box sx={{ marginTop: "10px" }}>
                {actionRows.map((row: any[], rowIdx: number) => (
                  <Box key={rowIdx} sx={{ display: "flex", gap: "8px", marginBottom: "8px", flexWrap: "wrap" }}>
                    {row.map((btn: any) => (
                      <Button
                        key={get(btn, "custom_id")}
                        variant="outlined"
                        size="small"
                        disabled={loading}
                        onClick={() => onAction(messageId, get(btn, "custom_id"))}
                      >
                        {get(btn, "emoji.name", "") ? `${get(btn, "emoji.name")} ` : ""}
                        {get(btn, "label", get(btn, "custom_id", ""))}
                      </Button>
                    ))}
                  </Box>
                ))}
              </Box>
            )}

            {!loading && !error && !result && (
              <Typography variant="body2" color="text.secondary">
                Enter a prompt and click Generate to create an image with Midjourney.
              </Typography>
            )}
          </Paper>

          {history.length > 0 && (
            <Paper sx={{ padding: "10px", marginTop: "10px" }}>
              <Typography variant="h6" sx={{ marginBottom: "10px" }}>
                History
              </Typography>
              {history.map((item: any, idx: number) => {
                const histContent = get(item, "content", "");
                const histAttachments = (get(item, "attachments") || []).filter(
                  (a: any) => get(a, "content_type", "").startsWith("image/")
                );
                return (
                  <Box key={get(item, "id", idx)} sx={{ marginBottom: "10px" }}>
                    {idx > 0 && <Divider sx={{ marginBottom: "10px" }} />}
                    <Typography variant="body2" sx={{ wordBreak: "break-word", marginBottom: "5px" }}>
                      {histContent}
                    </Typography>
                    {histAttachments.map((img: any) => (
                      <a key={get(img, "id")} href={get(img, "url")} target="_blank" rel="noopener noreferrer">
                        <img
                          src={get(img, "url")}
                          alt={get(img, "filename", "history image")}
                          loading="lazy"
                          style={{ maxWidth: "200px", cursor: "pointer", marginRight: "5px" }}
                        />
                      </a>
                    ))}
                  </Box>
                );
              })}
            </Paper>
          )}
        </Box>
      </Container>
    </Grid>
  );
};

export default MidjourneyView;
